import { API_URL_PATIENT_PORTRAIT } from '../../config/Environment';
import {
	getNakamaFetchClient,
	type DeepReadonly,
	type GetRequestPathParams,
	type GetResponseBodyJson,
} from '../openapiFetchClient';
import { getMemoizedApiMethodByPatientIdOnce } from './PatientPortraitApi.utils';

import type { paths as MutablePatientPortraitPaths } from './PatientPortraitApi.types.generated';

type PatientPortraitPaths = DeepReadonly<MutablePatientPortraitPaths>;
type NakamaFetchClient = ReturnType<typeof getNakamaFetchClient<PatientPortraitPaths>>;

const GET_PATIENT_DOCUMENTS_PATH = '/v1/patient/{patientId}/documents';
const GET_PATIENT_DEMOGRAPHICS_PATH = '/v1/patient/{patientId}/demographics';
const GET_PATIENT_LABS_PATH = '/v1/patient/{patientId}/labs';
const GET_PATIENT_PREVENTIVE_PATH = '/v1/patient/{patientId}/preventive';
const GET_PATIENT_PROBLEMS_PATH = '/v1/patient/{patientId}/problems';
const GET_PATIENT_MEDICATIONS_PATH = '/v1/patient/{patientId}/medications';
const GET_PATIENT_MEDICATIONS_V2_PATH = '/v2/patients/{patientId}/medications';
const GET_PATIENT_FAMILY_HISTORY_BY_ENTITY_PATH = '/v1/patient/{patientId}/history/family';
const GET_PATIENT_SURGICAL_HISTORY_BY_ENTITY_PATH = '/v1/patient/{patientId}/history/surgical';
const GET_PATIENT_IMAGING_AND_TESTS_PATH = '/v1/patient/{patientId}/image-and-test';
const GET_PATIENT_HOSPITALIZATION_PATH = '/v1/patient/{patientId}/hospitalization';
const GET_PATIENT_DATA_PATH = '/v1/patient/{patientId}';
const GET_PATIENT_APPOINTMENTS_PATH = '/v1/patient/{patientId}/appointments';
const GET_PATIENT_LAST_NAVINA_UPDATE_BY_ENTITY_PATH = '/v1/patient/{patientId}/last-navina-update/{entity}';
const GET_HEADER_PATIENT_ADJACENT_APPOINTMENTS_PATH = '/v1/header/patient/{patientId}/appointments/adjacent';
const GET_HEADER_PATIENT_APPOINTMENT_PATH = '/v1/header/patient/{patientId}/appointments/{appointmentId}';
const GET_HEADER_PATIENT_ALLERGIES_PATH = '/v1/header/patient/{patientId}/allergies';
const GET_HEADER_PATIENT_ORGANIZATION_PATH = '/v1/header/patient/{patientId}/organization';
const GET_HEADER_PATIENT_LAST_NAVINA_UPDATE_PATH = '/v1/header/patient/{patientId}/last-navina-update';
const GET_HEADER_PATIENT_STATUS_PATH = '/v1/header/patient/{patientId}/status';
const GET_HEADER_PATIENT_APPOINTMENT_SCRUBS_PATH = '/v1/header/patient/{patientId}/appointments/{appointmentId}/scrubs';
const GET_HEADER_LAST_WELLNESS_ENCOUNTER_PATH = '/v1/header/patient/{patientId}/last-wellness-encounter';
const GET_APPOINTMENT_PUSH_CONTEXT_PATH = '/v1/appointment/{patientId}/{appointmentId}/push-context';
const GET_APPOINTMENT_EMR_DETAILS_PATH = '/v1/appointment/{patientId}/{appointmentId}/emr-details';

type GetDocumentsByPatientIdRequest = GetRequestPathParams<PatientPortraitPaths, typeof GET_PATIENT_DOCUMENTS_PATH>;
export type GetDocumentsByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_PATIENT_DOCUMENTS_PATH
>;

type GetDemographicsByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_PATIENT_DEMOGRAPHICS_PATH
>;
export type GetDemographicsByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_PATIENT_DEMOGRAPHICS_PATH
>;

type GetLabsByPatientIdRequest = GetRequestPathParams<PatientPortraitPaths, typeof GET_PATIENT_LABS_PATH>;
export type GetLabsByPatientIdResponse = GetResponseBodyJson<PatientPortraitPaths, typeof GET_PATIENT_LABS_PATH>;

type GetPreventiveByPatientIdRequest = GetRequestPathParams<PatientPortraitPaths, typeof GET_PATIENT_PREVENTIVE_PATH>;
export type GetPreventiveByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_PATIENT_PREVENTIVE_PATH
>;

type GetProblemsByPatientIdRequest = GetRequestPathParams<PatientPortraitPaths, typeof GET_PATIENT_PROBLEMS_PATH>;
export type GetProblemsByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_PATIENT_PROBLEMS_PATH
>;

type GetMedicationsByPatientIdRequest = GetRequestPathParams<PatientPortraitPaths, typeof GET_PATIENT_MEDICATIONS_PATH>;
export type GetMedicationsByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_PATIENT_MEDICATIONS_PATH
>;

type GetMedicationsV2ByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_PATIENT_MEDICATIONS_V2_PATH
>;
export type GetMedicationsV2ByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_PATIENT_MEDICATIONS_V2_PATH
>;
export type MedicationV2 = GetMedicationsV2ByPatientIdResponse[number];

type GetFamilyHistoryByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_PATIENT_FAMILY_HISTORY_BY_ENTITY_PATH
>;
export type GetFamilyHistoryByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_PATIENT_FAMILY_HISTORY_BY_ENTITY_PATH
>;
export type FamilyHistoryEntry = GetFamilyHistoryByPatientIdResponse[number];

type GetSurgicalHistoryByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_PATIENT_SURGICAL_HISTORY_BY_ENTITY_PATH
>;
export type GetSurgicalHistoryByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_PATIENT_SURGICAL_HISTORY_BY_ENTITY_PATH
>;
export type SurgicalHistoryEntry = GetSurgicalHistoryByPatientIdResponse[number];

type GetImagingAndTestsByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_PATIENT_IMAGING_AND_TESTS_PATH
>;
export type GetImagingAndTestsByPatientIdResponse =
	PatientPortraitPaths[typeof GET_PATIENT_IMAGING_AND_TESTS_PATH]['get']['responses']['200']['content'];

type GetHospitalizationByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_PATIENT_HOSPITALIZATION_PATH
>;
export type GetHospitalizationByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_PATIENT_HOSPITALIZATION_PATH
>;

type GetPatientDataByPatientIdRequest = GetRequestPathParams<PatientPortraitPaths, typeof GET_PATIENT_DATA_PATH>;
type GetPatientDataByPatientIdResponse = GetResponseBodyJson<PatientPortraitPaths, typeof GET_PATIENT_DATA_PATH>;

type GetAppointmentsByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_PATIENT_APPOINTMENTS_PATH
>;
type GetAppointmentsByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_PATIENT_APPOINTMENTS_PATH
>;

type GetNavinaLastUpdateByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_PATIENT_LAST_NAVINA_UPDATE_BY_ENTITY_PATH
>;
type GetNavinaLastUpdateByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_PATIENT_LAST_NAVINA_UPDATE_BY_ENTITY_PATH
>;

type GetHeaderAdjacentAppointmentsByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_ADJACENT_APPOINTMENTS_PATH
>;
type GetHeaderAdjacentAppointmentsByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_ADJACENT_APPOINTMENTS_PATH
>;

type GetHeaderAppointmentByPatientIdAndAppointmentIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_APPOINTMENT_PATH
>;
export type GetHeaderAppointmentByPatientIdAndAppointmentIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_APPOINTMENT_PATH
>;

type GetHeaderAllergiesByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_ALLERGIES_PATH
>;
export type GetHeaderAllergiesByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_ALLERGIES_PATH
>;

type GetHeaderOrganizationByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_ORGANIZATION_PATH
>;
export type GetHeaderOrganizationByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_ORGANIZATION_PATH
>;

type GetHeaderLastNavinaUpdateByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_LAST_NAVINA_UPDATE_PATH
>;
type GetHeaderLastNavinaUpdateByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_LAST_NAVINA_UPDATE_PATH
>;

export type GetHeaderStatusByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_STATUS_PATH
>;
type GetHeaderStatusByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_STATUS_PATH
>;

type GetHeaderAppointmentScrubsByPatientIdAndAppointmentIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_APPOINTMENT_SCRUBS_PATH
>;
type GetHeaderAppointmentScrubsByPatientIdAndAppointmentIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_HEADER_PATIENT_APPOINTMENT_SCRUBS_PATH
>;

type GetHeaderLastWellnessEncounterByPatientIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_HEADER_LAST_WELLNESS_ENCOUNTER_PATH
>;
export type GetHeaderLastWellnessEncounterByPatientIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_HEADER_LAST_WELLNESS_ENCOUNTER_PATH
>;

type GetPushContextByPatientIdAndAppointmentIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_APPOINTMENT_PUSH_CONTEXT_PATH
>;
type GetPushContextByPatientIdAndAppointmentIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_APPOINTMENT_PUSH_CONTEXT_PATH
>;

type GetEmrDetailsByPatientIdAndAppointmentIdRequest = GetRequestPathParams<
	PatientPortraitPaths,
	typeof GET_APPOINTMENT_EMR_DETAILS_PATH
>;

type GetEmrDetailsByPatientIdAndAppointmentIdResponse = GetResponseBodyJson<
	PatientPortraitPaths,
	typeof GET_APPOINTMENT_EMR_DETAILS_PATH
>;
interface PatientPortraitApiProps {
	readonly client?: NakamaFetchClient;
}

const NAKAMA_SERVICE_NAME = 'nakama-patient-portrait';

class PatientPortraitApi {
	private readonly client: NakamaFetchClient;

	private constructor({
		client = getNakamaFetchClient<MutablePatientPortraitPaths>({
			baseUrl: API_URL_PATIENT_PORTRAIT,
			nakamaServiceName: NAKAMA_SERVICE_NAME,
		}),
	}: PatientPortraitApiProps) {
		this.client = client;
	}

	static readonly create = (props: PatientPortraitApiProps) => new PatientPortraitApi(props);

	readonly getDocumentsByPatientId = async (
		requestPayload: GetDocumentsByPatientIdRequest,
	): Promise<GetDocumentsByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_DOCUMENTS_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getDemographicsByPatientId = async (
		requestPayload: GetDemographicsByPatientIdRequest,
	): Promise<GetDemographicsByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_DEMOGRAPHICS_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getLabsByPatientId = async (
		requestPayload: GetLabsByPatientIdRequest,
	): Promise<GetLabsByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_LABS_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getPreventiveByPatientId = async (
		requestPayload: GetPreventiveByPatientIdRequest,
	): Promise<GetPreventiveByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_PREVENTIVE_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getProblemsByPatientId = async (
		requestPayload: GetProblemsByPatientIdRequest,
	): Promise<GetProblemsByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_PROBLEMS_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getMedicationsByPatientId = async (
		requestPayload: GetMedicationsByPatientIdRequest,
	): Promise<GetMedicationsByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_MEDICATIONS_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getMedicationsV2ByPatientId = async (
		requestPayload: GetMedicationsV2ByPatientIdRequest,
	): Promise<GetMedicationsV2ByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_MEDICATIONS_V2_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getFamilyHistoryByPatientId = async (
		requestPayload: GetFamilyHistoryByPatientIdRequest,
	): Promise<GetFamilyHistoryByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_FAMILY_HISTORY_BY_ENTITY_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getSurgicalHistoryByPatientId = async (
		requestPayload: GetSurgicalHistoryByPatientIdRequest,
	): Promise<GetSurgicalHistoryByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_SURGICAL_HISTORY_BY_ENTITY_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getImagingAndTestsByPatientId = async (
		requestPayload: GetImagingAndTestsByPatientIdRequest,
	): Promise<GetImagingAndTestsByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_IMAGING_AND_TESTS_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error);
		}

		return data;
	};

	readonly getHospitalizationByPatientId = async (
		requestPayload: GetHospitalizationByPatientIdRequest,
	): Promise<GetHospitalizationByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_HOSPITALIZATION_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getPatientDataByPatientId = async (
		requestPayload: GetPatientDataByPatientIdRequest,
	): Promise<GetPatientDataByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_DATA_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getAppointmentsByPatientId = async (
		requestPayload: GetAppointmentsByPatientIdRequest,
	): Promise<GetAppointmentsByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_APPOINTMENTS_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getNavinaLastUpdateByPatientId = async (
		requestPayload: GetNavinaLastUpdateByPatientIdRequest,
	): Promise<GetNavinaLastUpdateByPatientIdResponse> => {
		const response = await this.client.GET(GET_PATIENT_LAST_NAVINA_UPDATE_BY_ENTITY_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getHeaderAdjacentAppointmentsByPatientId = async (
		requestPayload: GetHeaderAdjacentAppointmentsByPatientIdRequest,
	): Promise<GetHeaderAdjacentAppointmentsByPatientIdResponse> => {
		const response = await this.client.GET(GET_HEADER_PATIENT_ADJACENT_APPOINTMENTS_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getHeaderAppointmentByPatientIdAndAppointmentId = async (
		requestPayload: GetHeaderAppointmentByPatientIdAndAppointmentIdRequest,
	): Promise<GetHeaderAppointmentByPatientIdAndAppointmentIdResponse> => {
		const response = await this.client.GET(GET_HEADER_PATIENT_APPOINTMENT_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getHeaderAllergiesByPatientId = async (
		requestPayload: GetHeaderAllergiesByPatientIdRequest,
	): Promise<GetHeaderAllergiesByPatientIdResponse> => {
		const response = await this.client.GET(GET_HEADER_PATIENT_ALLERGIES_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getHeaderOrganizationByPatientId = async (
		requestPayload: GetHeaderOrganizationByPatientIdRequest,
	): Promise<GetHeaderOrganizationByPatientIdResponse> => {
		const response = await this.client.GET(GET_HEADER_PATIENT_ORGANIZATION_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getHeaderLastNavinaUpdateByPatientId = async (
		requestPayload: GetHeaderLastNavinaUpdateByPatientIdRequest,
	): Promise<GetHeaderLastNavinaUpdateByPatientIdResponse> => {
		const response = await this.client.GET(GET_HEADER_PATIENT_LAST_NAVINA_UPDATE_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getHeaderStatusByPatientId = async (
		requestPayload: GetHeaderStatusByPatientIdRequest,
	): Promise<GetHeaderStatusByPatientIdResponse> => {
		const response = await this.client.GET(GET_HEADER_PATIENT_STATUS_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getHeaderAppointmentScrubsByPatientIdAndAppointmentId = async (
		requestPayload: GetHeaderAppointmentScrubsByPatientIdAndAppointmentIdRequest,
	): Promise<GetHeaderAppointmentScrubsByPatientIdAndAppointmentIdResponse> => {
		const response = await this.client.GET(GET_HEADER_PATIENT_APPOINTMENT_SCRUBS_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getHeaderLastWellnessEncounterByPatientId = async (
		requestPayload: GetHeaderLastWellnessEncounterByPatientIdRequest,
	): Promise<GetHeaderLastWellnessEncounterByPatientIdResponse> => {
		const response = await this.client.GET(GET_HEADER_LAST_WELLNESS_ENCOUNTER_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data, response: rawResponse } = response;

		if (error) {
			if (rawResponse.status === 404) {
				return {
					date: null,
				};
			}

			throw new Error(error.message);
		}

		return data;
	};

	readonly getEmrDetailsByPatientIdAndAppointmentId = async (
		requestPayload: GetEmrDetailsByPatientIdAndAppointmentIdRequest,
	): Promise<GetEmrDetailsByPatientIdAndAppointmentIdResponse> => {
		const response = await this.client.GET(GET_APPOINTMENT_EMR_DETAILS_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			throw new Error(error.message);
		}

		return data;
	};

	readonly getPushContextByPatientIdAndAppointmentId = async (
		requestPayload: GetPushContextByPatientIdAndAppointmentIdRequest,
	): Promise<GetPushContextByPatientIdAndAppointmentIdResponse> => {
		const response = await this.client.GET(GET_APPOINTMENT_PUSH_CONTEXT_PATH, {
			params: {
				path: requestPayload,
			},
		});

		const { error, data } = response;

		if (error) {
			console.warn('Failed getting push context', error.message);
			return {};
		}

		return data ?? {};
	};
}

let maybePatientPortraitApi: PatientPortraitApi | null = null;

export function getOrCreatePatientPortraitApi() {
	if (!maybePatientPortraitApi) {
		maybePatientPortraitApi = PatientPortraitApi.create({});
	}

	return maybePatientPortraitApi;
}

export const getDocumentsByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetDocumentsByPatientIdRequest,
	GetDocumentsByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getDocumentsByPatientId(request));

export const getDemographicsByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetDemographicsByPatientIdRequest,
	GetDemographicsByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getDemographicsByPatientId(request));

export const getPatientDataByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetPatientDataByPatientIdRequest,
	GetPatientDataByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getPatientDataByPatientId(request));

export const getPreventiveByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetPreventiveByPatientIdRequest,
	GetPreventiveByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getPreventiveByPatientId(request));

export const getMedicationsV2ByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetMedicationsV2ByPatientIdRequest,
	GetMedicationsV2ByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getMedicationsV2ByPatientId(request));

export const getProblemsByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetProblemsByPatientIdRequest,
	GetProblemsByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getProblemsByPatientId(request));

export const getLabsByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetLabsByPatientIdRequest,
	GetLabsByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getLabsByPatientId(request));

export const getHospitalizationByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetHospitalizationByPatientIdRequest,
	GetHospitalizationByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getHospitalizationByPatientId(request));

export const getImagingAndTestsByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetImagingAndTestsByPatientIdRequest,
	GetImagingAndTestsByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getImagingAndTestsByPatientId(request));

export const getFamilyHistoryByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetFamilyHistoryByPatientIdRequest,
	GetFamilyHistoryByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getFamilyHistoryByPatientId(request));

export const getSurgicalHistoryByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetSurgicalHistoryByPatientIdRequest,
	GetSurgicalHistoryByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getSurgicalHistoryByPatientId(request));

export const getHeaderAdjacentAppointmentsByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetHeaderAdjacentAppointmentsByPatientIdRequest,
	GetHeaderAdjacentAppointmentsByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getHeaderAdjacentAppointmentsByPatientId(request));

export const getHeaderAllergiesByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetHeaderAllergiesByPatientIdRequest,
	GetHeaderAllergiesByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getHeaderAllergiesByPatientId(request));

export const getHeaderOrganizationByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetHeaderOrganizationByPatientIdRequest,
	GetHeaderOrganizationByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getHeaderOrganizationByPatientId(request));

export const getHeaderLastNavinaUpdateByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetHeaderLastNavinaUpdateByPatientIdRequest,
	GetHeaderLastNavinaUpdateByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getHeaderLastNavinaUpdateByPatientId(request));

export const getHeaderStatusByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetHeaderStatusByPatientIdRequest,
	GetHeaderStatusByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getHeaderStatusByPatientId(request));

export const getHeaderAppointmentByPatientIdAndAppointmentIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetHeaderAppointmentByPatientIdAndAppointmentIdRequest,
	GetHeaderAppointmentByPatientIdAndAppointmentIdResponse
>((request) => getOrCreatePatientPortraitApi().getHeaderAppointmentByPatientIdAndAppointmentId(request));

export const getHeaderLastWellnessEncounterByPatientIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetHeaderLastWellnessEncounterByPatientIdRequest,
	GetHeaderLastWellnessEncounterByPatientIdResponse
>((request) => getOrCreatePatientPortraitApi().getHeaderLastWellnessEncounterByPatientId(request));

export const getEmrDetailsByPatientIdAndAppointmentIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetEmrDetailsByPatientIdAndAppointmentIdRequest,
	GetEmrDetailsByPatientIdAndAppointmentIdResponse
>((request) => getOrCreatePatientPortraitApi().getEmrDetailsByPatientIdAndAppointmentId(request));

export const getPushContextByPatientIdAndAppointmentIdOnce = getMemoizedApiMethodByPatientIdOnce<
	GetPushContextByPatientIdAndAppointmentIdRequest,
	GetPushContextByPatientIdAndAppointmentIdResponse
>((request) => getOrCreatePatientPortraitApi().getPushContextByPatientIdAndAppointmentId(request));
